/**
 * Deterministic Codebase Guide relevance for Work Brief create/detail.
 * No embeddings / model calls — string and path matching only.
 */

const PRIORITY_RANK = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

const CATEGORY_FALLBACK = new Set([
  "test_commands",
  "do_not_touch",
  "auth_security",
  "review_checklist",
  "freelancer_guidance",
]);

const CATEGORY_REASON = {
  test_commands: "Test command guidance",
  do_not_touch: "Do-not-touch guidance",
  auth_security: "Security guidance",
  review_checklist: "Important review checklist",
  freelancer_guidance: "Freelancer guidance",
};

function asLower(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function normalizePath(value) {
  return asLower(value).replace(/\\/g, "/");
}

function isVisibleToAudience(entry, audience = "both") {
  const visibility = entry?.visibility || { pm: true, engineer: true };
  if (audience === "pm") return visibility.pm !== false;
  if (audience === "engineer") return visibility.engineer !== false;
  return visibility.pm !== false || visibility.engineer !== false;
}

function collectTextBlob(workBrief, draftRequestText = "") {
  const parts = [];
  if (draftRequestText) parts.push(draftRequestText);
  if (!workBrief) return asLower(parts.join("\n"));

  parts.push(workBrief.featureRequestText);
  parts.push(workBrief.title);

  const intelligence = workBrief.planIntelligence || {};
  parts.push(intelligence.goal);
  parts.push(intelligence.pmSummary);
  parts.push(intelligence.engineerSummary);
  parts.push(intelligence.businessValue);

  if (Array.isArray(workBrief.acceptanceCriteria)) {
    parts.push(...workBrief.acceptanceCriteria);
  }

  const expected = intelligence.expectedChanges;
  if (expected && typeof expected === "object") {
    for (const key of ["backend", "frontend", "dataModel", "tests"]) {
      if (Array.isArray(expected[key])) parts.push(...expected[key]);
    }
  }

  return asLower(parts.filter(Boolean).join("\n"));
}

function collectPathSignals(workBrief) {
  if (!workBrief) return [];
  const paths = [];

  if (Array.isArray(workBrief.coreImplementationFiles)) {
    paths.push(...workBrief.coreImplementationFiles);
  }
  if (Array.isArray(workBrief.excludedFiles)) {
    paths.push(...workBrief.excludedFiles);
  }

  const files = workBrief.planIntelligence?.files;
  if (files && typeof files === "object") {
    for (const role of ["primary", "secondary", "risky", "avoid"]) {
      for (const item of files[role] || []) {
        if (item?.path) paths.push(item.path);
      }
    }
  }

  const expected = workBrief.planIntelligence?.expectedChanges;
  if (expected && typeof expected === "object") {
    for (const key of ["backend", "frontend", "dataModel", "tests"]) {
      for (const item of expected[key] || []) {
        if (typeof item === "string") paths.push(item);
      }
    }
  }

  return paths.map(normalizePath).filter(Boolean);
}

function pathScopeMatches(scopeItem, pathSignals) {
  const scope = normalizePath(scopeItem);
  if (!scope) return false;
  return pathSignals.some((path) => {
    return (
      path === scope ||
      path.startsWith(scope) ||
      scope.startsWith(path) ||
      path.includes(scope) ||
      scope.includes(path)
    );
  });
}

function taskScopeMatches(scopeItem, textBlob) {
  const term = asLower(scopeItem);
  if (!term || term.length < 2) return false;
  return textBlob.includes(term);
}

/**
 * @param {Array} entries - Codebase Guide entries
 * @param {object|null} workBrief - FeaturePlan-like object (detail/handoff) or null (create)
 * @param {object} [options]
 * @param {'create'|'detail'|'handoff'} [options.mode]
 * @param {'pm'|'engineer'|'both'} [options.audience]
 * @param {number} [options.limit]
 * @param {string} [options.draftRequestText] - typed request on create form
 * @returns {{ entries: Array, reasonsById: Record<string, string[]> }}
 */
export function getRelevantCodebaseGuideEntries(entries, workBrief, options = {}) {
  const {
    mode = "detail",
    audience = "both",
    limit = 8,
    draftRequestText = "",
  } = options;

  const textBlob = collectTextBlob(
    workBrief,
    mode === "create" ? draftRequestText : draftRequestText || ""
  );
  const pathSignals = collectPathSignals(workBrief);

  const candidates = (Array.isArray(entries) ? entries : []).filter(
    (entry) => entry && entry.isActive !== false && isVisibleToAudience(entry, audience)
  );

  const scored = [];

  for (const entry of candidates) {
    const reasons = [];
    let score = 0;
    const pathScope = Array.isArray(entry.pathScope) ? entry.pathScope : [];
    const taskScope = Array.isArray(entry.taskTypeScope) ? entry.taskTypeScope : [];
    const isGlobal = pathScope.length === 0 && taskScope.length === 0;
    const priorityRank = PRIORITY_RANK[entry.priority] || 1;
    const isHighPriority =
      entry.priority === "high" || entry.priority === "critical";

    let pathHit = false;
    if (pathScope.length > 0 && pathSignals.length > 0) {
      pathHit = pathScope.some((scope) => pathScopeMatches(scope, pathSignals));
      if (pathHit) {
        reasons.push("Matches expected files");
        score += 100 + priorityRank * 10;
      }
    }

    let taskHit = false;
    if (taskScope.length > 0 && textBlob) {
      taskHit = taskScope.some((scope) => taskScopeMatches(scope, textBlob));
      if (taskHit) {
        reasons.push("Matches task type");
        score += 80 + priorityRank * 10;
      }
    }

    if (isGlobal && isHighPriority) {
      reasons.push("High-priority repo rule");
      score += 60 + priorityRank * 10;
    }

    // Category fallback: useful global operating rules when exact match is weak.
    if (
      isGlobal &&
      CATEGORY_FALLBACK.has(entry.category) &&
      priorityRank >= PRIORITY_RANK.medium &&
      !reasons.includes("High-priority repo rule")
    ) {
      reasons.push(CATEGORY_REASON[entry.category] || "Important category guidance");
      score += 30 + priorityRank * 5;
    }

    // Path-scoped rules with no path signals: do not force (unless already matched above).
    // Task-scoped with no text: skip unless other reasons already applied.

    if (reasons.length === 0) continue;

    scored.push({
      entry,
      reasons: Array.from(new Set(reasons)),
      score,
      priorityRank,
    });
  }

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (b.priorityRank !== a.priorityRank) return b.priorityRank - a.priorityRank;
    return String(a.entry.title || "").localeCompare(String(b.entry.title || ""));
  });

  const limited = scored.slice(0, Math.max(0, limit));
  const reasonsById = {};
  const resultEntries = limited.map((item) => {
    const id = String(item.entry._id);
    reasonsById[id] = item.reasons;
    return {
      ...item.entry,
      matchReasons: item.reasons,
    };
  });

  return { entries: resultEntries, reasonsById };
}

export const __testables = {
  collectTextBlob,
  collectPathSignals,
  pathScopeMatches,
  taskScopeMatches,
  isVisibleToAudience,
  PRIORITY_RANK,
  CATEGORY_FALLBACK,
};
